import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  TextInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  CheckboxGroupInput,
  required,
  maxLength,
  useDataProvider,
  useNotify,
  useTranslate,
  useLocaleState,
} from 'react-admin';
import {
  Box,
  Button as MuiButton,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { EventTypeSelector } from '../event-fields/EventTypeSelector';
import { VenueSelector } from '../event-fields/VenueSelector';
import { CategorySelector } from '../event-fields/CategorySelector';
import { PAYMENT_METHODS, VALIDATION_RULES } from '../../../constants/events';
import { shouldFieldBeDisabled, getFieldHelperText } from '../../../utils/eventFormUtils';
import { slugify } from '../../../utils/string';
import { SourceLanguageBanner } from '../SourceLanguageBanner';
import { deeplService } from '../../../services/deeplService';

/**
 * Event Basic Details Tab Component
 * Shared tab for basic event information
 */
export const EventBasicDetailsTab = ({
  eventType,
  dateType,
  onEventTypeChange,
  onDateTypeChange,
  isEdit = false,
  permissions = {},
  record = {},
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const isAdmin = permissions?.crole === 'admin';
  const isOperator = permissions?.crole === 'operator';
  const isOrganisationManager = permissions?.crole === 'organisationCompanyManager';

  const paymentChoices = React.useMemo(() => {
    const raw = isEdit
      ? PAYMENT_METHODS
      : PAYMENT_METHODS.filter((method) => method.id !== 'doorcash');
    return raw.map((m) => ({
      ...m,
      name: translate(`form.payment_method_${m.id}`, { _: m.name }),
    }));
  }, [isEdit, translate, locale]);

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      {isEdit && (
        <>
          <h3>{translate('form.pre_selected_types', { _: 'Pre Selected Types' })}</h3>
          <EventTypeSelector
            eventType={eventType}
            dateType={dateType}
            onEventTypeChange={onEventTypeChange}
            onDateTypeChange={onDateTypeChange}
            disabled={true}
          />
        </>
      )}

      <h3>{translate('form.basic_details', { _: 'Basic Details' })}</h3>

      <EventNameInput />
      <EventSlugInput record={record} />

      <VenueSelector />

      <ReferenceArrayInput
        source="organisationCompanies"
        reference="organisationCompanies"
        label={translate('form.organisation_company', { _: 'Organizasyon Şirketi' })}
        defaultValue={[]}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <AutocompleteArrayInput
          fullWidth
          label={translate('form.organisation_company', { _: 'Organizasyon Şirketi' })}
          matchSuggestion={() => true}
          filterToQuery={(searchText) => ({ name: searchText })}
          optionText="name"
          placeholder={translate('form.select_organisation_company', {
            _: 'Organizasyon şirketi seçin…',
          })}
          disabled={isOrganisationManager}
          value={isOrganisationManager ? [permissions.rid] : undefined}
          defaultValue={isOrganisationManager ? [permissions.rid] : []}
        />
      </ReferenceArrayInput>

      <LiabilityOwnerCheckbox />

      <CategorySelector />

      {(isAdmin || isOperator) && isEdit && (
        <React.Fragment>
          <EventBadgeLabelInputs />
          <TextInput
            source="badge.textColor"
            label={translate('form.label_color', { _: 'Label Color' })}
            fullWidth
            defaultValue=""
          />
          <TextInput
            source="badge.backgroundColor"
            label={translate('form.background_color', { _: 'Background Color' })}
            fullWidth
            defaultValue=""
          />
        </React.Fragment>
      )}

      <h3>{translate('form.payment_methods', { _: 'Payment Methods' })}</h3>
      <CheckboxGroupInput
        source="paymentMethods"
        defaultValue={['cc']}
        choices={paymentChoices}
        validate={required()}
      />
    </React.Fragment>
  );
};

const DEFAULT_BADGE_LANGUAGES = [
  { code: 'tr', nativeName: 'Türkçe' },
  { code: 'en', nativeName: 'English' },
];

const badgeLabelValidators = [
  maxLength(
    VALIDATION_RULES.BADGE_LABEL.maxLength,
    'The label should be at most 23 characters long.',
  ),
];

/**
 * Rozet metni — navbar dilinde tek input; DeepL ile diğer aktif dillere çevrilip altta listelenir.
 */
const EventBadgeLabelInputs = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const sourceLang = (locale || 'tr').toLowerCase();
  const { getValues, setValue } = useFormContext();
  const [languages, setLanguages] = React.useState(DEFAULT_BADGE_LANGUAGES);
  const [translating, setTranslating] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    deeplService
      .getActiveLanguages()
      .then((rows) => {
        if (cancelled || !Array.isArray(rows) || !rows.length) return;
        setLanguages(
          rows
            .map((l) => ({
              code: String(l.code || '').toLowerCase(),
              nativeName: l.nativeName || l.name || l.code,
            }))
            .filter((l) => l.code),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const current = getValues('badge.label');
    if (typeof current === 'string') {
      setValue('badge.label', current ? { [sourceLang]: current } : {});
    }
  }, [getValues, setValue, sourceLang]);

  const otherLanguages = React.useMemo(
    () => languages.filter((l) => l.code !== sourceLang),
    [languages, sourceLang],
  );

  const sourceLanguageName =
    languages.find((l) => l.code === sourceLang)?.nativeName || sourceLang.toUpperCase();

  const handleTranslate = async () => {
    const current = getValues('badge.label');
    const currentObj = current && typeof current === 'object' ? current : {};
    const text = String(currentObj[sourceLang] || '').trim();
    if (!text) {
      notify(
        translate('form.badge_translate_empty', {
          _: 'Önce %{lang} rozet metnini yazın.',
          lang: sourceLanguageName,
        }),
        { type: 'warning' },
      );
      return;
    }

    setTranslating(true);
    try {
      const res = await deeplService.translateAll(text, sourceLang);
      const translations = res?.translations || {};
      setValue(
        'badge.label',
        { ...currentObj, ...translations },
        { shouldDirty: true, shouldValidate: true },
      );
      notify(
        translate('form.badge_translate_success', {
          _: 'Rozet tüm dillere çevrildi. Kaydetmeyi unutmayın.',
        }),
        { type: 'success' },
      );
    } catch (error) {
      const fallback = translate('form.badge_translate_error', {
        _: 'DeepL çevirisi başarısız oldu.',
      });
      const message = error?.message || fallback;
      notify(message, { type: 'error', messageArgs: { _: message } });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <h3 style={{ margin: '16px 0 8px' }}>
          {translate('form.event_badge', { _: 'Event Badge' })}
        </h3>
        <MuiButton
          size="small"
          variant="outlined"
          startIcon={translating ? <CircularProgress size={14} /> : <TranslateIcon />}
          onClick={handleTranslate}
          disabled={translating}
        >
          {translate('form.badge_translate_button', { _: 'DeepL ile tüm dillere çevir' })}
        </MuiButton>
      </Box>
      <TextInput
        source={`badge.label.${sourceLang}`}
        label={`${translate('form.badge_label', { _: 'Badge Label' })} (${sourceLanguageName})`}
        fullWidth
        defaultValue=""
        validate={badgeLabelValidators}
      />
      {otherLanguages.length > 0 && (
        <Box sx={{ pl: 2, borderLeft: '3px solid #e5e7eb', mb: 2 }}>
          <Box sx={{ fontSize: 12, color: '#6b7280', mb: 1 }}>
            {translate('form.badge_translations', { _: 'Çeviriler' })}
          </Box>
          {otherLanguages.map((lang) => (
            <TextInput
              key={lang.code}
              source={`badge.label.${lang.code}`}
              label={lang.nativeName}
              size="small"
              fullWidth
              defaultValue=""
              validate={badgeLabelValidators}
            />
          ))}
        </Box>
      )}
    </React.Fragment>
  );
};

/**
 * Mekan + organizasyon şirketi birlikte seçiliyse yükümlülüğün kime ait olduğunu sorar.
 * liabilityOwner: 'organisation' | 'venue' | null
 */
const LiabilityOwnerCheckbox = () => {
  const translate = useTranslate();
  const { setValue, control } = useFormContext();
  const venue = useWatch({ control, name: 'venue' });
  const orgs = useWatch({ control, name: 'organisationCompanies' });
  const owner = useWatch({ control, name: 'liabilityOwner' });

  const hasVenue = Boolean(venue);
  const hasOrg = Array.isArray(orgs) && orgs.length > 0;
  const showChoice = hasVenue && hasOrg;

  React.useEffect(() => {
    let next = owner ?? null;
    if (showChoice) {
      if (next !== 'organisation') next = 'venue';
    } else if (hasOrg) {
      next = 'organisation';
    } else if (hasVenue) {
      next = 'venue';
    } else {
      next = null;
    }
    if (next !== (owner ?? null)) {
      setValue('liabilityOwner', next, { shouldDirty: true });
    }
  }, [showChoice, hasOrg, hasVenue, owner, setValue]);

  if (!showChoice) return null;

  return (
    <FormControlLabel
      sx={{ mt: -0.5, mb: 1 }}
      control={
        <Checkbox
          size="small"
          checked={owner === 'organisation'}
          onChange={(e) =>
            setValue('liabilityOwner', e.target.checked ? 'organisation' : 'venue', {
              shouldDirty: true,
            })
          }
        />
      }
      label={translate('form.liability_on_organisation', {
        _: 'Yükümlülük organizasyon şirketine ait (işaretlenmezse mekana ait)',
      })}
    />
  );
};

/**
 * Event Name Input Component
 * Handles event name input with auto-capitalization
 */
const EventNameInput = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();

  const handleNameChange = (e) => {
    setValue('name', e.target.value.toLocaleUpperCase('tr-TR'));
  };

  return (
    <TextInput
      source="name"
      label={translate('form.event_name', { _: 'Event Name' })}
      fullWidth
      validate={required()}
      onBlur={handleNameChange}
    />
  );
};

const EventSlugInput = ({ record }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { setValue, control } = useFormContext();
  const name = useWatch({ control, name: 'name' });
  const slug = useWatch({ control, name: 'slug' });
  const manuallyEdited = React.useRef(Boolean(record?.slug));

  React.useEffect(() => {
    if (manuallyEdited.current) return;
    const generated = slugify(name);
    if (generated !== slug) {
      setValue('slug', generated, { shouldDirty: Boolean(name), shouldValidate: false });
    }
  }, [name, slug, setValue]);

  const validateUniqueSlug = React.useCallback(
    async (value) => {
      const normalized = slugify(value);
      if (!normalized) return 'Slug is required.';
      if (normalized !== value) {
        return 'Use only lowercase letters, numbers and hyphens.';
      }

      try {
        const { data } = await dataProvider.getList('events-v2', {
          filter: { slug: normalized },
          pagination: { page: 1, perPage: 2 },
          sort: { field: 'createdate', order: 'DESC' },
        });
        const conflict = data.some((event) => {
          const sameRecord =
            event.id === record?.id ||
            (record?.uid && (event.uid === record.uid || event.id === record.uid));
          return !sameRecord;
        });
        return conflict ? 'This slug is already used by another event.' : undefined;
      } catch {
        return 'Slug availability could not be checked.';
      }
    },
    [dataProvider, record?.id, record?.uid],
  );

  const normalizeSlug = () => {
    manuallyEdited.current = true;
    setValue('slug', slugify(slug), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <TextInput
      source="slug"
      label={translate('form.unique_event_slug', { _: 'Unique Event Slug' })}
      fullWidth
      validate={[required(), validateUniqueSlug]}
      onFocus={() => {
        manuallyEdited.current = true;
      }}
      onBlur={normalizeSlug}
      helperText={translate('form.slug_helper_text', {
        _: 'Unique URL slug: only lowercase a-z, 0-9 and hyphens. No Turkish characters.',
      })}
      inputProps={{
        autoComplete: 'off',
        spellCheck: false,
        style: { textTransform: 'lowercase' },
      }}
    />
  );
};
